import { SELECT_ALL } from "commons/table/Constants";
import { POPULATION_TYPE_OPTIONS } from "lookup/PopulationTypeConstants";
import { isCollectionNullOrEmpty } from "services/UtilityService";
import { AgentWorkspaceConstants } from "lookup/AgentWorkspaceConstants";

/** react-select may return one option or an array depending on isMulti. */
export function normalizeSelectValue(selected) {
  if (selected == null) {
    return [];
  }
  return Array.isArray(selected) ? selected : [selected];
}

/**
 * Builds the {@code filtered} array for {@code POST /agent-workspace/call-group/next}.
 * Values are abbreviation / enum strings, matching the list API shape.
 *
 * @param {Record<string, object|object[]|null>} selections react-select values by filter id
 * @returns {{id: string, value: string[]}[]}
 */
export function buildNextCallGroupFiltered(selections) {
  const filtered = [];
  for (const id of AgentWorkspaceConstants.AGENT_NEXT_CALL_GROUP_FILTER_IDS) {
    const values = normalizeSelectValue(selections[id])
      .map((option) => option.value)
      .filter((value) => (id === "clientNameAbbreviation" ? value !== SELECT_ALL : true));
    if (isCollectionNullOrEmpty(values)) {
      continue;
    }
    filtered.push({ id, value: values });
  }
  return filtered;
}

/**
 * Maps list-filters option rows to react-select `{ value, label }` options.
 */
export function toSelectOptions(filterRows) {
  if (!filterRows) {
    return [];
  }
  return filterRows.map((row) => ({
    value: row.id,
    label: row.value,
  }));
}

/** Static population type options for the agent filter screen (matches admin list values). */
export function populationTypeSelectOptions() {
  return POPULATION_TYPE_OPTIONS.map((o) => ({
    value: o.value,
    label: o.label,
  }));
}

function populationTypeValue(row) {
  const pt = row?.populationType;
  if (pt == null) {
    return null;
  }
  return typeof pt === "string" ? pt : pt.name || pt.value || null;
}

/**
 * Restricts client options by selected customers and/or population types.
 * Does not include the SELECT_ALL marker — callers prepend that for the UI.
 */
export function clientOptionsForFilters(
  filtersMeta,
  selectedCustomers,
  selectedPopulationTypes
) {
  let clientRows = filtersMeta?.clientNameAbbreviation || [];

  const customers = normalizeSelectValue(selectedCustomers);
  if (!isCollectionNullOrEmpty(customers)) {
    const selectedCustomerIds = customers.map((c) => c.value);
    const mapping = (filtersMeta?.customerClientMapping || []).filter((m) =>
      selectedCustomerIds.includes(m.id)
    );
    const allowedClientIds = new Set(
      mapping.reduce((result, entry) => result.concat(entry.value || []), [])
    );
    clientRows = clientRows.filter((client) => allowedClientIds.has(client.id));
  }

  const populationTypes = normalizeSelectValue(selectedPopulationTypes).map(
    (opt) => opt.value
  );
  if (!isCollectionNullOrEmpty(populationTypes)) {
    clientRows = clientRows.filter((client) => {
      const pt = populationTypeValue(client);
      return pt && populationTypes.includes(pt);
    });
  }

  return clientRows.map((row) => ({
    value: row.id,
    label: row.value,
  }));
}

/** @deprecated Prefer {@link clientOptionsForFilters}; kept for callers that only pass customers. */
export function clientOptionsForCustomers(filtersMeta, selectedCustomers) {
  return clientOptionsForFilters(filtersMeta, selectedCustomers, []);
}

/** Client dropdown options including the ALL marker (same behavior as Call Group List). */
export function clientOptionsWithSelectAll(
  filtersMeta,
  selectedCustomers,
  selectedPopulationTypes
) {
  return [
    { value: SELECT_ALL, label: SELECT_ALL },
    ...clientOptionsForFilters(
      filtersMeta,
      selectedCustomers,
      selectedPopulationTypes
    ),
  ];
}

/**
 * Resolves Client multi-select changes when the ALL option is involved.
 * Mirrors commons MultiSelectAll: picking ALL selects every real client;
 * clearing ALL clears the selection; ALL is never kept as a selected value.
 */
export function resolveClientSelectionChange(selected, actionMeta, clientOptions) {
  const { action, option, removedValue } = actionMeta || {};
  const realClients = (clientOptions || []).filter((opt) => opt.value !== SELECT_ALL);

  if (action === "select-option" && option?.value === SELECT_ALL) {
    return realClients;
  }
  if (
    (action === "deselect-option" || action === "remove-value") &&
    (removedValue?.value === SELECT_ALL || option?.value === SELECT_ALL)
  ) {
    return [];
  }
  return normalizeSelectValue(selected).filter((opt) => opt.value !== SELECT_ALL);
}
